/**
 * EventTypeBadge component for displaying event types with colours
 * Shared component used in both EventCard and EventDetail
 */

/**
 * Event type interface compatible with GraphQL types
 * Supports both TermInterface (without eventTypeColor) and TermEventType (with eventTypeColor)
 */
export interface EventType {
  id: string;
  name: string;
  eventTypeColor?: string | null;
}

interface EventTypeBadgeProps {
  type: EventType;
  variant?: 'default' | 'card';
  className?: string;
}

/**
 * Single event type badge
 */
export function EventTypeBadge({ type, variant = 'default', className = '' }: EventTypeBadgeProps) {
  if (variant === 'card') {
    // Card variant with border-left accent
    // Ensure eventTypeColor is a valid string (not null or undefined)
    const eventTypeColor =
      type.eventTypeColor && typeof type.eventTypeColor === 'string' ? type.eventTypeColor : null;
    const borderColor = eventTypeColor || 'hsl(var(--primary))';
    const backgroundColor = eventTypeColor ? `${eventTypeColor}20` : 'hsl(var(--primary) / 0.1)';

    return (
      <span
        className={`rounded px-1.5 py-0.5 text-[10px] font-medium shadow-md sm:px-2 sm:py-1 sm:text-xs ${className}`}
        style={{
          backgroundColor,
          borderRight: `3px solid ${borderColor}`,
        }}
      >
        {type.name}
      </span>
    );
  }

  // Default variant (rounded-full)
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${className}`}
      style={{
        backgroundColor: type.eventTypeColor ? `${type.eventTypeColor}20` : '#f0f0f0',
        color: type.eventTypeColor || '#666',
      }}
    >
      {type.name}
    </span>
  );
}

interface EventTypesProps {
  eventTypes: EventType[] | EventType | null | undefined;
  variant?: 'default' | 'card';
  className?: string;
}

/**
 * Display multiple event types
 */
export function EventTypes({ eventTypes, variant = 'default', className = '' }: EventTypesProps) {
  const typesArray = Array.isArray(eventTypes) ? eventTypes : eventTypes ? [eventTypes] : [];

  if (typesArray.length === 0) {
    return null;
  }

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {typesArray.map(type =>
        type?.id && type?.name ? (
          <EventTypeBadge key={type.id} type={type} variant={variant} />
        ) : null
      )}
    </div>
  );
}
